import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  AgentChatClient,
  ChatMessage,
  ChatSession,
  AgentConfig,
} from '@/services/agentChatClient';
import { createAgentChatClient } from '@/services/agentChatClient';

interface AgentChatState {
  // Configuration
  agentConfig: AgentConfig | null;
  client: AgentChatClient | null;

  // Session state
  sessions: ChatSession[];
  currentSessionId: string | null;
  sessionsLoading: boolean;
  sessionsError: string | null;

  // Messages state
  messages: ChatMessage[];
  messagesLoading: boolean;

  // Streaming state
  isStreaming: boolean;
  streamingContent: string;

  // Actions
  initialize: (config: AgentConfig) => void;
  loadSessions: () => Promise<void>;
  createNewSession: () => void;
  switchSession: (sessionId: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  cancelStream: () => void;
  clearError: () => void;
}

export const useAgentChatStore = create<AgentChatState>()(
  immer((set, get) => ({
    // Initial state
    agentConfig: null,
    client: null,
    sessions: [],
    currentSessionId: null,
    sessionsLoading: false,
    sessionsError: null,
    messages: [],
    messagesLoading: false,
    isStreaming: false,
    streamingContent: '',

    initialize: (config: AgentConfig) => {
      const client = createAgentChatClient(config);
      set({
        agentConfig: config,
        client,
        sessions: [],
        currentSessionId: null,
        messages: [],
        sessionsError: null,
      });
    },

    loadSessions: async () => {
      const { client } = get();
      if (!client) return;

      set({ sessionsLoading: true, sessionsError: null });

      try {
        const sessions = await client.listSessions({ limit: 50 });
        set({ sessions, sessionsLoading: false });
      } catch (error) {
        set({
          sessionsError: error instanceof Error ? error.message : 'Failed to load sessions',
          sessionsLoading: false,
        });
      }
    },

    createNewSession: () => {
      const { client } = get();
      if (!client) return;

      // Clear the context ID on the client to start a new run/context
      // The next message sent will create a NEW context on the server
      // and the server will return a new contextId (runId)
      client.startNewSession();
      set({
        currentSessionId: null,
        messages: [],
        streamingContent: '',
      });
    },

    switchSession: async (sessionId: string) => {
      const { client, currentSessionId, messages } = get();
      if (!client) return;

      // If we're already on this session and have messages, don't reload
      // This preserves local state (like streaming messages) during transitions
      if (currentSessionId === sessionId && messages.length > 0) {
        client.setContextId(sessionId);
        return;
      }

      set({ messagesLoading: true, currentSessionId: sessionId });

      try {
        // Set the context ID (runId) on the client
        // This ensures subsequent messages are sent to this context
        client.setContextId(sessionId);
        const loadedMessages = await client.loadSessionMessages(sessionId);
        set({ messages: loadedMessages, messagesLoading: false });
      } catch (error) {
        // On error loading messages, keep existing messages if we have them
        // This prevents losing local conversation state on transient errors
        const currentMessages = get().messages;
        set({
          sessionsError: error instanceof Error ? error.message : 'Failed to load messages',
          messagesLoading: false,
          // Only clear messages if we had none to begin with
          messages: currentMessages.length > 0 ? currentMessages : [],
        });
      }
    },

    sendMessage: async (content: string) => {
      const { client, currentSessionId } = get();
      if (!client) return;

      // Add user message immediately
      // If currentSessionId is set, the message will be sent to that context (run)
      // If currentSessionId is null, a NEW context will be created on the server
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date(),
        contextId: currentSessionId ?? undefined,
      };

      set((state) => {
        state.messages.push(userMessage);
        state.isStreaming = true;
        state.streamingContent = '';
      });

      // Create placeholder for assistant message
      // Track current message ID - this can change when we receive new messages
      let currentAssistantMessageId = `assistant-${Date.now()}`;
      set((state) => {
        state.messages.push({
          id: currentAssistantMessageId,
          role: 'assistant',
          content: '',
          timestamp: new Date(),
          isStreaming: true,
        });
      });

      try {
        await client.sendMessage(
          content,
          (update) => {
            set((state) => {
              // If this is a new message (replacing previous content), finalize
              // the current message and create a new one
              if (update.isNewMessage) {
                // Find and finalize the current assistant message
                const currentMsgIndex = state.messages.findIndex(
                  (m: ChatMessage) => m.id === currentAssistantMessageId
                );
                const currentMsg = state.messages[currentMsgIndex];
                if (currentMsgIndex >= 0 && currentMsg) {
                  currentMsg.isStreaming = false;
                }

                // Create a new assistant message for the new content
                currentAssistantMessageId = `assistant-${Date.now()}-${Math.random().toString(36).slice(2)}`;
                state.messages.push({
                  id: currentAssistantMessageId,
                  role: 'assistant',
                  content: update.content,
                  timestamp: new Date(),
                  isStreaming: !update.isComplete,
                  contextId: update.contextId,
                });
              } else {
                // Update the existing assistant message
                const msgIndex = state.messages.findIndex(
                  (m: ChatMessage) => m.id === currentAssistantMessageId
                );
                const assistantMsg = state.messages[msgIndex];
                if (msgIndex >= 0 && assistantMsg) {
                  assistantMsg.content = update.content;
                  assistantMsg.isStreaming = !update.isComplete;

                  if (update.contextId) {
                    assistantMsg.contextId = update.contextId;
                  }
                }
              }

              // Update the streaming content
              state.streamingContent = update.content;

              // Update context ID on user message and session state
              if (update.contextId) {
                // Update user message context
                const userMsgIndex = state.messages.findIndex(
                  (m: ChatMessage) => m.id === userMessage.id
                );
                const userMsg = state.messages[userMsgIndex];
                if (userMsgIndex >= 0 && userMsg) {
                  userMsg.contextId = update.contextId;
                }

                // Update current session ID (contextId/runId) IMMEDIATELY when we receive it
                // This allows the UI to transition to monitoring view right away,
                // so users can see tool executions etc. before the agent finishes responding
                if (!state.currentSessionId) {
                  state.currentSessionId = update.contextId;
                }
              }

              if (update.isComplete) {
                state.isStreaming = false;
                state.streamingContent = '';

                // Remove assistant message when task failed - don't show "No content" for failures
                // The failure is logged to console by agentChatClient
                if (update.taskState === 'failed') {
                  const assistantMsgIndex = state.messages.findIndex(
                    (m: ChatMessage) => m.id === currentAssistantMessageId
                  );
                  if (assistantMsgIndex >= 0) {
                    state.messages.splice(assistantMsgIndex, 1);
                  }
                }
              }
            });
          },
          (error) => {
            set((state) => {
              state.isStreaming = false;
              state.streamingContent = '';
              state.sessionsError = error.message;

              // Mark the assistant message as errored
              const msgIndex = state.messages.findIndex((m: ChatMessage) => m.id === currentAssistantMessageId);
              const errorMsg = state.messages[msgIndex];
              if (msgIndex >= 0 && errorMsg) {
                errorMsg.content = 'Error: ' + error.message;
                errorMsg.isStreaming = false;
              }
            });
          }
        );
      } catch (error) {
        set((state) => {
          state.isStreaming = false;
          state.streamingContent = '';
          state.sessionsError = error instanceof Error ? error.message : 'Failed to send message';
        });
      }
    },

    cancelStream: () => {
      const { client } = get();
      if (!client) return;

      client.cancelStream();
      set((state) => {
        state.isStreaming = false;
        state.streamingContent = '';

        // Mark any streaming messages as complete
        for (const msg of state.messages) {
          if (msg.isStreaming) {
            msg.isStreaming = false;
          }
        }
      });
    },

    clearError: () => {
      set({ sessionsError: null });
    },
  }))
);
